/** The emissions-by-month card every emissions band uses - ADR-0212, package P9.
 *
 * Bars stacked by the scopes handed in, in the colour each scope has on every page (`scope1`,
 * `scope2`, `scope3`). What is still arriving arrives already marked per scope
 * (`bandModel.barMonths`), so the chart draws only the affected segment pale and never as a real
 * fall. A month opens that month's figures (`onOpenMonth`: the Every figure drawer, on the same
 * page). The unit is on the title row and the Table view is the card's. **One card draws the monthly
 * series on Home, the Executive dashboard, the Emissions screen and the Scope pages**, so the same
 * months are never told two ways.
 */

import type { BarMonth, BarSeries } from "../../components/charts";
import { MonthlyBars } from "../../components/charts";
import { BandCard } from "./BandCard";
import type { BandRead } from "./BandCard";
import type { ScopeKey } from "./bandModel";

const SERIES: Record<ScopeKey, BarSeries> = {
  "1": { key: "1", label: "Scope 1", colour: "scope1" },
  "2": { key: "2", label: "Scope 2", colour: "scope2" },
  "3": { key: "3", label: "Scope 3", colour: "scope3" },
};

export function MonthlyCard({
  title = "Emissions by month",
  scopes,
  bars,
  reads,
  onOpenMonth,
  testId,
  unreconciled,
  estimates,
}: {
  title?: string;
  /** The scopes that were assessed, in stacking order. */
  scopes: readonly ScopeKey[];
  bars: readonly BarMonth[] | undefined;
  reads: readonly BandRead[];
  /** Opens the figures of a month (`YYYY-MM`). Absent where there is nothing honest to open (a month of
   * one site: the drawer lists every site's), and then the bars are not a way in. */
  onOpenMonth?: (month: string) => void;
  testId?: string;
  /** Set when the months do not add up to the window total: nothing is drawn rather than a chart
   * that disagrees with the figures beside it. */
  unreconciled?: string;
  /** How much of the shown total rests on estimates, in tonnes, as a legend entry. */
  estimates?: string;
}) {
  return (
    <BandCard
      title={title}
      unit="tCO2e"
      reads={reads}
      hasTable={unreconciled === undefined}
      {...(testId ? { testId } : {})}
    >
      {unreconciled !== undefined ? (
        <p role="alert" data-testid="band-monthly-unreconciled">
          The monthly series is not shown because it does not add up to the window total ({unreconciled}).
        </p>
      ) : bars ? (
        <MonthlyBars
          months={bars}
          series={scopes.map((scope) => SERIES[scope])}
          unit="tCO2e"
          caption={title}
          height={200}
          testId="band-monthly"
          {...(estimates ? { legendNote: estimates } : {})}
          {...(onOpenMonth
            ? {
                onOpenReceipt: (month: string) => {
                  onOpenMonth(month);
                },
              }
            : {})}
        />
      ) : null}
    </BandCard>
  );
}
